import { api } from '../api';
import {
  INBOX_NOTIFICATIONS,
  INBOX_PAGE_SIZE,
  INBOX_READ_ALL,
  INBOX_UNREAD_COUNT,
  inboxReadNotificationUrl
} from '../../constants/notifications';
import {
  GetNotificationsRequest,
  GetNotificationsResponse,
  GetUnreadCountResponse,
  ReadAllNotificationsResponse
} from './types';

export const notificationSlice = api.injectEndpoints({
  endpoints: (build) => ({
    // ========================================================================
    // LIST — GET rtc/inbox/notifications?page_size=&page=
    //
    // 1-based numbered pages, no cursor. A page past the end is not an error:
    // 200 with an empty list, totals still filled in.
    // ========================================================================
    getNotifications: build.query<
      GetNotificationsResponse,
      GetNotificationsRequest
    >({
      query: ({ page, pageSize = INBOX_PAGE_SIZE }) => ({
        url: INBOX_NOTIFICATIONS,
        method: 'GET',
        // page_size is sent ahead of page, matching what the backend was
        // verified against.
        params: { page_size: pageSize, page }
      }),
      providesTags: (_result, _error, { page }) => [
        { type: 'Notifications' as const, id: `PAGE_${page}` },
        { type: 'Notifications' as const, id: 'LIST' }
      ]
    }),

    // ========================================================================
    // UNREAD COUNT — the only inbox call the bell makes.
    // ========================================================================
    getUnreadCount: build.query<GetUnreadCountResponse, void>({
      query: () => ({
        url: INBOX_UNREAD_COUNT,
        method: 'GET'
      }),
      providesTags: [{ type: 'Notifications' as const, id: 'UNREAD_COUNT' }]
    }),

    // ========================================================================
    // READ ALL — one call per visit, instead of one call per row.
    // ========================================================================
    readAllNotifications: build.mutation<ReadAllNotificationsResponse, void>({
      query: () => ({
        url: INBOX_READ_ALL,
        method: 'POST'
      }),
      // The badge clears the moment the call goes out rather than after the
      // count refetches; a failed call puts the old count back.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          notificationSlice.util.updateQueryData(
            'getUnreadCount',
            undefined,
            (draft) => {
              if (!draft.data) draft.data = {};
              draft.data.unreadCount = '0';
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      // Deliberately does NOT invalidate the list: rows keep the `readAt` the
      // server sent, so unread dots stay visible while the user reads them.
      // Returning later refetches and they come back read.
      invalidatesTags: [{ type: 'Notifications' as const, id: 'UNREAD_COUNT' }]
    }),

    // ========================================================================
    // READ ONE — POST rtc/inbox/notifications/{id}/read. Idempotent.
    //
    // The bell shows five rows, so it marks those five rather than the whole
    // inbox: read-all there cleared unread rows the user had never seen. Like
    // read-all, it leaves the list alone so the dots stay while on screen.
    // ========================================================================
    readNotification: build.mutation<void, string>({
      query: (notificationId) => ({
        url: inboxReadNotificationUrl(notificationId),
        method: 'POST'
      }),
      invalidatesTags: [{ type: 'Notifications' as const, id: 'UNREAD_COUNT' }]
    })
  }),
  overrideExisting: false
});

export const {
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
  useReadAllNotificationsMutation,
  useReadNotificationMutation
} = notificationSlice;
